import { useEffect, useState } from 'react'
import ProgressionService from '../services/api/ProgressionService'

// Stable default so an absent resourceIds does not change identity each render
// (which would otherwise retrigger the effect endlessly).
const EMPTY_RESOURCE_IDS = []

// Same reason: pages narrowing the fetched metrics must pass a stable array.
const ALL_MEASURES = ['grade', 'satisfaction', 'controls', 'additional_controls', 'alerts']

const INITIAL = {
  grade: null,
  satisfaction: null,
  controlsCount: null,
  additionalControlsCount: null,
  expectedControlsCount: null,
  alertsCount: null,
  alertsRate: null
}

const extractValue = payload => {
  if (!payload || Array.isArray(payload))
    return null
  const value = payload.value ?? payload.count
  return Number.isFinite(value) ? value : null
}

// Total controls = standard controls + additional controls (separate measure).
const totalControls = (controls, additional) => {
  const base = extractValue(controls)
  if (base === null)
    return null
  return base + (extractValue(additional) || 0)
}

// Fetches the KPI metrics (note, satisfaction, controls, alerts) at the "total" granularity.
// Shared by the Statistics, Evolution and Progression metric rows.
// Passing `measures` narrows the requests to what the page actually displays.
const useKpiMeasures = (collectId, draftId, params) => {
  const [values, setValues] = useState(INITIAL)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const {
    startDate, endDate, operatorId, measures = ALL_MEASURES,
    resourceIds = EMPTY_RESOURCE_IDS, resourceSearch, variableId
  } = params

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const measureParams = {
      startDate, endDate, granularity: 'total', operatorId, resourceIds, resourceSearch, variableId
    }
    const measureFor = name =>
      ProgressionService.fetchMeasure(collectId, draftId, { name, ...measureParams })
    Promise.all(measures.map(measureFor))
      .then(payloads => {
        if (cancelled)
          return
        const byName = new Map(measures.map((name, index) => [name, payloads[index]]))
        setValues({
          grade: extractValue(byName.get('grade')),
          satisfaction: extractValue(byName.get('satisfaction')),
          controlsCount: totalControls(byName.get('controls'), byName.get('additional_controls')),
          additionalControlsCount: extractValue(byName.get('additional_controls')),
          expectedControlsCount: extractValue(byName.get('expected_controls')),
          alertsCount: extractValue(byName.get('alerts')),
          alertsRate: extractValue(byName.get('alerts_rate'))
        })
        setError(false)
      })
      .catch(() => { if (!cancelled) {
        setValues(INITIAL)
        setError(true)
      } })
      .finally(() => { if (!cancelled)
        setLoading(false) })
    return () => { cancelled = true }
  }, [collectId, draftId, startDate, endDate, operatorId, resourceIds, resourceSearch, variableId, measures])

  return { values, loading, error }
}

export default useKpiMeasures
